import React, { useState } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import { FiEye, FiEyeOff, FiMail, FiLock, FiAlertCircle, FiSun } from 'react-icons/fi';
import './Login.css';

const Login = () => {
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const { signIn } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Get redirect path from location state or default to dashboard
  const from = location.state?.from?.pathname || '/dashboard';

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error when user starts typing
    if (error) setError('');
  };

  const handleEmailPasswordLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      await signIn(formData.email, formData.password);
      navigate(from, { replace: true });
    } catch (err) {
      let friendlyErrorMessage = 'Failed to login. Please check your credentials.';
      
      switch (err.code) {
        case 'auth/invalid-email':
          friendlyErrorMessage = 'Invalid email format.';
          break;
        case 'auth/user-not-found':
        case 'auth/wrong-password':
        case 'auth/invalid-credential':
          friendlyErrorMessage = 'Invalid email or password.';
          break;
        case 'auth/too-many-requests':
          friendlyErrorMessage = 'Too many failed attempts. Please try again later.';
          break;
        case 'auth/user-disabled':
          friendlyErrorMessage = 'This account has been disabled. Please contact support.';
          break;
        default:
          console.error('Login error:', err);
          friendlyErrorMessage = 'An unexpected error occurred during login.';
      }
      setError(friendlyErrorMessage);
    } finally {
      setLoading(false);
    }
  };



  return (
    <div className="login-page">
      <div className="login-container">
        <div className="login-content">
          {/* Logo and Branding */}
          <div className="login-header">
            <img src="/Block Logo-01.png" alt="Block Logo" className="login-logo" />
            <h1 className="login-title">Welcome Back</h1>
            <p className="login-subtitle">Sign in to your Block account</p>
          </div>

          {/* Error Display */}
          {error && (
            <div className="error-alert">
              <FiAlertCircle className="error-icon" />
              <span>{error}</span>
            </div>
          )}



          {/* Email/Password Form */}
          <form onSubmit={handleEmailPasswordLogin} className="login-form">
            <div className="form-group">
              <div className="input-wrapper">
                <input
                  type="email"
                  name="email"
                  className="form-input"
                  placeholder="Email address"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                  disabled={loading}
                  autoComplete="email"
                />
              </div>
            </div>

            <div className="form-group">
              <div className="input-wrapper">
                <input
                  type={showPassword ? 'text' : 'password'}
                  name="password"
                  className="form-input"
                  placeholder="Password"
                  value={formData.password}
                  onChange={handleInputChange}
                  required
                  disabled={loading}
                  autoComplete="current-password"
                />
                <button
                  type="button"
                  className="password-toggle"
                  onClick={() => setShowPassword(!showPassword)}
                  disabled={loading}
                >
                  {showPassword ? <FiEyeOff /> : <FiEye />}
                </button>
              </div>
            </div>

            <div className="form-options">
              <Link to="/auth/forgot-password" className="forgot-password-link">
                Forgot your password?
              </Link>
            </div>

            <button
              type="submit"
              className="login-button"
              disabled={loading || !formData.email || !formData.password}
            >
              {loading ? (
                <div className="button-loading">
                  <FiSun className="spinner-small" />
                  <span>Signing in...</span>
                </div>
              ) : (
                'Sign In'
              )}
            </button>
          </form>

          {/* Footer */}
          <div className="login-footer">
            <p>
              Need help?{' '}
              <Link to="/contact" className="register-link">
                Contact us
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login; 